import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Skeleton } from "@mui/material";
import ContextFilterAndSort from "../ContextFilterAndSort/ContextFilterAndSort";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import "./ListBoxes.css";
import { useTranslation } from "react-i18next";
import { UI_TEXT } from "../../constants";

const useDebounce = (value, delay = 300) => {
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
}

const ListboxesSkeleton = () => {
	return (
		<>
			<div className="user-cus-site-selection">
				{/* Customer List Box Skeleton */}
				<div className="list-box">
					<Box pb={2}>
						<Skeleton
							variant="rectangular"
							height={40}
							width="100%"
							sx={{ borderRadius: "10px" }}
						/>
					</Box>
					<div className="list-box-container">
						{Array.from({ length: 6 }).map((_, index) => (
							<div className="list-box-item" key={index}>
								<Skeleton
									width="80%"
									height={20}
									sx={{ mb: 1, borderRadius: "10px" }}
								/>
								<Skeleton
									width="60%"
									height={16}
									sx={{ borderRadius: "10px" }}
								/>
							</div>
						))}
					</div>
				</div>

				{/* Sites List Box Skeleton */}
				<div className="list-box">
					<Box pb={2}>
						<Skeleton
							variant="rectangular"
							height={40}
							width="100%"
							sx={{ borderRadius: "10px" }}
						/>
					</Box>
					<div className="list-box-container">
						<div className="list-box-item-header">
							<Skeleton width="70%" height={22} sx={{ borderRadius: "10px" }} />
						</div>
						{Array.from({ length: 6 }).map((_, index) => (
							<div className="list-box-item" key={index}>
								<Skeleton
									width="80%"
									height={20}
									sx={{ mb: 1, borderRadius: "10px" }}
								/>
								<Skeleton
									width="60%"
									height={16}
									sx={{ borderRadius: "10px" }}
								/>
							</div>
						))}
					</div>
				</div>

				{/* Chevron Buttons Skeleton */}
				<div
					className="list-controls"
					style={{ display: "flex", flexDirection: "column", gap: "8px" }}
				>
					<Button
						variant="outlined"
						color="primary"
						disabled

					>
						<ChevronRight />
					</Button>
					<Button
						variant="outlined"
						color="primary"
						disabled

					>
						<ChevronLeft />
					</Button>
				</div>

				{/* Selected Sites List Box Skeleton */}
				<div className="list-box">
					<div className="list-box-container" style={{ minHeight: "490px" }}>
						<div className="list-box-item-header">
							<Skeleton width="60%" height={22} sx={{ borderRadius: "10px" }} />
						</div>
						{Array.from({ length: 6 }).map((_, index) => (
							<div className="list-box-item" key={index}>
								<Skeleton
									width="80%"
									height={20}
									sx={{ mb: 1, borderRadius: "10px" }}
								/>
								<Skeleton
									width="60%"
									height={16}
									sx={{ borderRadius: "10px" }}
								/>
							</div>
						))}
					</div>
				</div>
			</div>
		</>
	);
};


const Listboxes = ({ customers = [], sites = [], assignedSitesProp = [], onAssignedSitesChange, multipleCustomersAllowed = true, multipleSitesAllowed = true }) => {
	const [customerSelected, setCustomerSelected] = useState(null);
	const [customerSearchTerm, setCustomerSearchTerm] = useState("");
	const [sitesSearchTerm, setSitesSearchTerm] = useState("");
	const [loading, setLoading] = useState(true);
	// Debounced values
	const debouncedCustomerSearchTerm = useDebounce(customerSearchTerm, 400);
	const debouncedSitesSearchTerm = useDebounce(sitesSearchTerm, 400);

	const [siteSelected, setSiteSelected] = useState([]);
	const [filteredSites, setFilteredSites] = useState([]);
	const [selectedSites, setSelectedSites] = useState({});
	const [assignedSites, setAssignedSites] = useState(assignedSitesProp);
	const [selectedSitesToMove, setSelectedSitesToMove] = useState([]);
    const {t} = useTranslation();
    const singleCustomer = !multipleCustomersAllowed;
    const singleSitePerCustomer = !multipleSitesAllowed;

    const assignedSummary = useMemo(() => {
        const totalCustomers = assignedSites.length;
        const first = assignedSites[0] || null;
        const totalSitesInFirst = first ? (first.sites || []).length : 0;
        return { totalCustomers, firstCustomerId: first?.customer || null, totalSitesInFirst };
    }, [assignedSites]);

    const disableAllSelection = singleCustomer && singleSitePerCustomer && assignedSummary.totalCustomers === 1 && assignedSummary.totalSitesInFirst === 1;

	// useEffect(() => {
	//     if (assignedSitesProp && assignedSitesProp.length > 0) {
	//         const initialSelectedSites = {};
	//         assignedSitesProp.forEach((assigned) => {
	//             const customer = customers.find((c) => c.cus_id === assigned.customer);
	//             if (customer) {
	//                 initialSelectedSites[customer.cus_id] = {
	//                     customer,
	//                     // Only sites belonging to this customer and in the assigned list
	//                     sites: sites.filter((site) => site.cus_id === customer.cus_id && assigned.sites.includes(site.site_id)),
	//                 };
	//             }
	//         });

	// 		// Set the selected sites for pre-filling
	// 		setSelectedSites(initialSelectedSites);
	// 		setAssignedSites(assignedSitesProp);

	// 		// Filter out the assigned sites from the available sites
	//         const assignedSiteIds = assignedSitesProp.flatMap((assigned) => assigned.sites);
	//         const remainingSites = sites.filter((site) => !assignedSiteIds.includes(site.site_id));
	//         setFilteredSites(remainingSites);
	//     } else {
	// 		// Reset selections; show all sites as available
	// 		setSelectedSites({});
	// 		setAssignedSites([]);
	// 		setFilteredSites(sites);
	// 	}
	// }, [assignedSitesProp, customers, sites]);

	const assignedSitesPropRef = React.useRef(assignedSitesProp);
	useEffect(() => {
		assignedSitesPropRef.current = assignedSitesProp;
	}, [assignedSitesProp]);

	//  Initialize data synchronously and avoid repeated loading loops
	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		const initialize = () => {
			if (cancelled) return;
			if (assignedSitesProp && assignedSitesProp.length > 0) {
				const initialSelectedSites = {};
				assignedSitesProp.forEach((assigned) => {
					const customer = customers.find((c) => c.cus_id === assigned.customer);
					if (customer) {
						initialSelectedSites[customer.cus_id] = {
							customer,
							sites: sites.filter(
								(site) =>
									site.cus_id === customer.cus_id &&
									assigned.sites.includes(site.site_id)
							),
						};
					}
				});
				setSelectedSites(initialSelectedSites);
				setAssignedSites(assignedSitesProp);

				const assignedSiteIds = assignedSitesProp.flatMap((assigned) => assigned.sites);
				const remainingSites = sites.filter((site) => !assignedSiteIds.includes(site.site_id));
				setFilteredSites(remainingSites);
			} else {
				setSelectedSites({});
				setAssignedSites([]);
				setFilteredSites(sites);
			}
			if (!cancelled) {
				setLoading(false);
			}
		};
		initialize();
		return () => {
			cancelled = true;
		};
	}, [assignedSitesProp, customers, sites]);

	// Normalize assigned sites for stable comparison (ignore ordering)
	const normalizeAssigned = React.useCallback((arr) => {
		if (!Array.isArray(arr)) return [];
		return arr
			.map((a) => ({
				customer: a.customer,
				sites: Array.isArray(a.sites) ? [...a.sites].sort((x, y) => (x > y ? 1 : x < y ? -1 : 0)) : [],
			}))
			.sort((a, b) => (a.customer > b.customer ? 1 : a.customer < b.customer ? -1 : 0));
	}, []);

	// Prevent feedback loop: only notify parent when local changes due to user actions.
	useEffect(() => {
		if (!onAssignedSitesChange) return;
		const localNorm = normalizeAssigned(assignedSites);
		const propNorm = normalizeAssigned(assignedSitesPropRef.current);
		const changed = JSON.stringify(localNorm) !== JSON.stringify(propNorm);
		if (changed) {
			onAssignedSitesChange(assignedSites);
		}
	}, [assignedSites, onAssignedSitesChange, normalizeAssigned]);

	// Track previous assignedSitesProp to compare for changes

    const handleCustomerListItemClick = (item) => {
        if (!item) return;
        if (singleCustomer && assignedSummary.totalCustomers >= 1 && item.cus_id !== assignedSummary.firstCustomerId) return;
        setCustomerSelected(item);
    const customerSites = sites.filter((site) => site && site.cus_id === item.cus_id);
    const selectedSet = new Set((selectedSites[item.cus_id]?.sites || []).map((s) => s.site_id));
    const remainingCustomerSites = customerSites.filter((site) => !selectedSet.has(site.site_id));
		setFilteredSites(remainingCustomerSites);
		setSiteSelected([]);
	};

    const handleSiteListItemClick = (item) => {
        if (!item) return;
        if (singleSitePerCustomer) {
            const custId = customerSelected?.cus_id;
            if (!custId) return;
            const alreadyCount = (selectedSites[custId]?.sites || []).length + siteSelected.length;
            if (alreadyCount >= 1 && !siteSelected.includes(item)) return;
        }
        setSiteSelected((prevSiteSelected) =>
            prevSiteSelected.includes(item)
                ? prevSiteSelected.filter((selected) => selected !== item)
                : [...prevSiteSelected, item]
        );
    };

	const handleSelectedSiteClick = (site) => {
		setSelectedSitesToMove((prevSelectedSites) => {
			if (prevSelectedSites.includes(site)) {
				return prevSelectedSites.filter((s) => s !== site);
			} else {
				return [...prevSelectedSites, site];
			}
		});
	};

	const handleCustomerSearchChange = (value) => {
		setCustomerSearchTerm(value);
	};

	const handleSitesSearchChange = (value) => {
		setSitesSearchTerm(value);
	};

	const filteredCustomers = useMemo(() => {
		const term = debouncedCustomerSearchTerm.toLowerCase();
		return customers.filter(
			(item) =>
				item &&
				(item.customer_name?.toLowerCase().includes(term) ||
					item.customer_id?.toLowerCase().includes(term))
		);
	}, [customers, debouncedCustomerSearchTerm]);

	const filteredSitesSearch = useMemo(() => {
		const term = debouncedSitesSearchTerm.toLowerCase();
		return filteredSites.filter(
			(item) =>
				item &&
				(item.customer_site_name?.toLowerCase().includes(term) ||
					item.customer_site_number?.toLowerCase().includes(term))
		);
	}, [filteredSites, debouncedSitesSearchTerm]);


    const handleMoveToSelectedSites = () => {
        if (!customerSelected || siteSelected.length === 0) return;
        if (singleCustomer && assignedSummary.totalCustomers >= 1 && customerSelected.cus_id !== assignedSummary.firstCustomerId) return;

        let sitesToAdd = [...siteSelected];
        if (singleSitePerCustomer) {
            const currentCount = (selectedSites[customerSelected.cus_id]?.sites || [])
                .concat((assignedSites.find(a => a.customer === customerSelected.cus_id)?.sites || []).map(id => ({ site_id: id }))).length;
            if (currentCount >= 1) {
                sitesToAdd = [];
            } else {
                sitesToAdd = sitesToAdd.slice(0, 1);
            }
        }
        if (sitesToAdd.length === 0) return;

		setSelectedSites((prevSelectedSites) => {
			const newSelectedSites = { ...prevSelectedSites };
			if (!newSelectedSites[customerSelected.cus_id]) {
				newSelectedSites[customerSelected.cus_id] = {
					customer: customerSelected,
					sites: [],
				};
			}
            newSelectedSites[customerSelected.cus_id].sites = [
                ...new Set([...newSelectedSites[customerSelected.cus_id].sites, ...sitesToAdd]),
            ];

			return newSelectedSites;
		});

		setAssignedSites((prevAssignedSites) => {
			const updatedAssignedSites = [...prevAssignedSites];
			const customerAssigned = updatedAssignedSites.find(
				(assigned) => assigned.customer === customerSelected.cus_id
			);

            if (customerAssigned) {
                customerAssigned.sites = [
                    ...new Set([...customerAssigned.sites, ...sitesToAdd.map((site) => site.site_id)]),
                ];
            } else {
                updatedAssignedSites.push({
                    customer: customerSelected.cus_id,
                    sites: sitesToAdd.map((site) => site.site_id),
                });
            }
            return updatedAssignedSites;
        });

        setFilteredSites((prevFilteredSites) => prevFilteredSites.filter((site) => !sitesToAdd.includes(site)));
        setSiteSelected([]);
        setCustomerSelected(null);
    };

    const canMoveRight = useMemo(() => {
        if (!customerSelected || siteSelected.length === 0) return false;
        if (disableAllSelection) return false;
        if (singleCustomer && assignedSummary.totalCustomers >= 1 && customerSelected.cus_id !== assignedSummary.firstCustomerId) return false;
        if (singleSitePerCustomer) {
            const currentCount = (selectedSites[customerSelected.cus_id]?.sites || [])
                .concat((assignedSites.find(a => a.customer === customerSelected.cus_id)?.sites || []).map(id => ({ site_id: id }))).length;
            if (currentCount >= 1) return false;
        }
        return true;
    }, [customerSelected, siteSelected, disableAllSelection, singleCustomer, assignedSummary, selectedSites, assignedSites, singleSitePerCustomer]);

	const handleMoveToFilteredSites = () => {
		if (selectedSitesToMove.length === 0) return;

		setFilteredSites((prevFilteredSites) => [...prevFilteredSites, ...selectedSitesToMove]);

		if (customerSelected) {
			setSelectedSites((prevSelectedSites) => {
				const newSelectedSites = { ...prevSelectedSites };
				newSelectedSites[customerSelected.cus_id].sites = newSelectedSites[
					customerSelected.cus_id
				].sites.filter((site) => !selectedSitesToMove.includes(site));

				if (newSelectedSites[customerSelected.cus_id].sites.length === 0) {
					delete newSelectedSites[customerSelected.cus_id];
				}

				return newSelectedSites;
			});

			setAssignedSites((prevAssignedSites) =>
				prevAssignedSites
					.map((assigned) =>
						assigned.customer === customerSelected?.cus_id
							? {
									...assigned,
									sites: assigned.sites.filter(
										(id) => !selectedSitesToMove.map((s) => s.site_id).includes(id)
									),
							  }
							: assigned
					)
					.filter((assigned) => assigned.sites.length > 0)
			);
		} else {
			setAssignedSites(
				(prevAssignedSites) =>
					prevAssignedSites
						.map((assigned) => ({
							...assigned,
							sites: assigned.sites.filter(
								(id) => !selectedSitesToMove.some((selectedSite) => selectedSite.site_id === id)
							),
						}))
						.filter((assigned) => assigned.sites.length > 0) // Remove customers with no sites left
			);
			setSelectedSites((prevSelectedSites) => {
				const newSelectedSites = { ...prevSelectedSites };
				Object.keys(newSelectedSites).forEach((cusId) => {
					newSelectedSites[cusId].sites = newSelectedSites[cusId].sites.filter(
						(site) => !selectedSitesToMove.some((s) => s.site_id === site.site_id)
					);

					if (newSelectedSites[cusId].sites.length === 0) {
						delete newSelectedSites[cusId];
					}
				});
				return newSelectedSites;
			});
		}

		setSelectedSitesToMove([]);
	};

	if (loading) {
		return (
			<ListboxesSkeleton />
		);
	}

	return (
		<>
			<div className="user-cus-site-selection">
				{/* Customer List Box */}
				<div className="list-box">
					<Box pb={2}>
						<ContextFilterAndSort
							value={customerSearchTerm}
							onSearchChange={handleCustomerSearchChange}
							placeholder={"Search Customers"}
							showSearch={true}
							showSort={false}
						/>
					</Box>
                    <div className="list-box-container" style={disableAllSelection ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                        {filteredCustomers.map((item, index) => {
                            const disabled = singleCustomer && assignedSummary.totalCustomers >= 1 && item.cus_id !== assignedSummary.firstCustomerId;
                            return (
                            <div
                                key={index}
                                className={`list-box-item ${customerSelected === item ? "selected" : ""}`}
                                style={disabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}
                                onClick={() => handleCustomerListItemClick(item)}>
                                <p className="title">{item.customer_name}</p>
                                <p className="description">{item.customer_number || item.customer_id}</p>
                            </div>
                        )})}
                    </div>
                </div>

				{/* Sites List Box */}
				<div className="list-box">
					<Box pb={2}>
						<ContextFilterAndSort
							value={sitesSearchTerm}
							onSearchChange={handleSitesSearchChange}
							placeholder={t('GENERAL.SEARCH_CUSTOMER_LOCATIONS')}
							showSearch={true}
							showSort={false}
						/>
					</Box>
                    <div className="list-box-container" style={disableAllSelection ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                        <div className="list-box-item-header">
							<p className="title">
								{customerSelected
									? `${customerSelected.cus_id} - ${customerSelected.customer_name}`
									: t('UI_TEXT.PLEASE_SELECT_CUSTOMER')}
							</p>
						</div>
						{customerSelected &&
							filteredSitesSearch.map((item, index) => (
								<div
									key={index}
									className={`list-box-item ${siteSelected.includes(item) ? "selected" : ""}`}
									onClick={() => handleSiteListItemClick(item)}>
									<p className="title">{item.customer_site_name}</p>
									<p className="description">{item.customer_site_number}</p>
								</div>
							))}
					</div>
				</div>

				{/* Chevron Buttons */}
                <div className="list-controls">
                    <Button variant="outlined" color="primary" onClick={handleMoveToSelectedSites} disabled={!canMoveRight}>
                        <ChevronRight />
                    </Button>
					<Button variant="outlined" color="primary" onClick={handleMoveToFilteredSites}>
						<ChevronLeft />
					</Button>
				</div>

				{/* Selected Sites List Box */}
				<div className="list-box">
					<div className="list-box-container" style={{ minHeight: "490px" }}>
						{Object.keys(selectedSites).map((cus_id) => (
							<React.Fragment key={cus_id}>
								<div className="list-box-item-header">
									<p className="title">
										{selectedSites[cus_id].customer.customer_number} -{" "}
										{selectedSites[cus_id].customer.customer_name}
									</p>
								</div>
								{selectedSites[cus_id].sites.map((site) => (
									<div
										key={site.site_id}
										className={`list-box-item ${
											selectedSitesToMove.includes(site) ? "selected" : ""
										}`}
										onClick={() => handleSelectedSiteClick(site)}>
										<p className="title">{site.customer_site_name}</p>
										<p className="description">{site.customer_site_number}</p>
									</div>
								))}
							</React.Fragment>
						))}
					</div>
				</div>
			</div>
		</>
	);
};

export default Listboxes;
